import { DataWarehouseConnection } from '../workflow/connections'
import { FilterWorkflowComponent } from './common/filtercomponent'
import {
  ColumnDataType,
  Provider,
  TableSchema,
  WorkflowCodeType,
  WorkflowComponentParamType,
  WorkflowEnvVars
} from '../types'
import { Workflow } from '../workflow'
import dedent from 'ts-dedent'
import { formatValue, getValueTypeFromSchema } from '../utils'

const operators = new Map([
  ['equal to', 'Is equal to...'],
  ['not equal', 'Is not equal to...'],
  ['greater than', 'Is greater than...'],
  ['equal or greater than', 'Is greater than or equal to...'],
  ['less than', 'Is less than...'],
  ['equal or less than', 'Is less than or equal to...']
])

const operatorSymbol = {
  'equal to': '=',
  'not equal': '!=',
  'greater than': '>',
  'equal or greater than': '>=',
  'less than': '<',
  'equal or less than': '<='
}

class WhereSimplified extends FilterWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.wheresimplified',
        group: 'Data Preparation',
        title: 'Simple Filter',
        description: `
      This component filters an input table according to a filter expression based on a single column.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'column',
          type: WorkflowComponentParamType.Column,
          title: 'Column',
          parent: 'source',
          dataType: [
            ColumnDataType.String,
            ColumnDataType.Number,
            ColumnDataType.Date,
            ColumnDataType.Datetime,
            ColumnDataType.Time,
            ColumnDataType.Timestamp,
            ColumnDataType.Boolean
          ]
        },
        {
          name: 'operator',
          type: WorkflowComponentParamType.Selection,
          title: 'Operator',
          options: Array.from(operators.keys()),
          optionsText: Array.from(operators.values())
        },
        {
          name: 'value',
          type: WorkflowComponentParamType.String,
          title: 'Value'
        }
      ]
    )
  }

  async _getMatchCode(
    params: Record<string, unknown>,
    envVars: WorkflowEnvVars,
    workflow: Workflow,
    componentName: string,
    connection: DataWarehouseConnection,
    codeType: WorkflowCodeType,
    schema?: TableSchema
  ) {
    if (codeType === WorkflowCodeType.DryRun) {
      return dedent`SELECT *
        FROM ${connection.quoteName(params.source as string)}`
    }
    let value = params.value
    if (!workflow.isExpression(componentName, 'value')) {
      const valueType = getValueTypeFromSchema(params.column as string, schema)
      value = formatValue(params.value as string, connection.type, valueType)
    }
    return dedent`SELECT *
      FROM ${connection.quoteName(params.source as string)}
      WHERE
        ${params.column} ${operatorSymbol[params.operator as string]} ${value}`
  }

  async _getUnmatchCode(
    params: Record<string, unknown>,
    matchTablename: string,
    envVars: WorkflowEnvVars,
    workflow: Workflow,
    componentName: string,
    connection: DataWarehouseConnection,
    codeType: WorkflowCodeType,
    schema?: TableSchema
  ) {
    if (codeType === WorkflowCodeType.DryRun) {
      if (connection.type === Provider.DatabricksRest) {
        return dedent`SELECT /*+ REPARTITION(40) */
            *
          FROM ${connection.quoteName(params.source as string)}`
      } else {
        return dedent`SELECT *
          FROM ${connection.quoteName(params.source as string)}`
      }
    }
    let value = params.value
    if (!workflow.isExpression(componentName, 'value')) {
      const valueType = getValueTypeFromSchema(params.column as string, schema)
      value = formatValue(params.value as string, connection.type, valueType)
    }
    if (connection.type === Provider.DatabricksRest) {
      return dedent`SELECT /*+ REPARTITION(40) */
          *
        FROM ${connection.quoteName(params.source as string)}
        WHERE NOT
          ${params.column} ${operatorSymbol[params.operator as string]} ${value}`
    } else {
      return dedent`SELECT *
        FROM ${connection.quoteName(params.source as string)}
        WHERE NOT
          ${params.column} ${operatorSymbol[params.operator as string]} ${value}`
    }
  }
  supportedProviders(): Provider[] {
    return [
      Provider.BigQuery,
      Provider.Postgres,
      Provider.Redshift,
      Provider.Snowflake,
      Provider.DatabricksRest,
      Provider.Oracle
    ]
  }
}

export { WhereSimplified }
